/**
 * MoveModal (DE-139, DE-160) — shared between OpsDeviceGroups and OpsDevices.
 *
 * Combined env-group + tenant move for a single device. Either or both
 * fields can be left on "don't change" — the modal gates each mutation
 * (useSetDeviceGroup, useSetDeviceTenant) on whether the corresponding
 * target actually differs from the device's current value, runs the
 * needed mutations in parallel via Promise.allSettled, and reports
 * per-mutation partial failure so the operator can retry the failed
 * side without re-doing the half that already landed.
 *
 * Defaults to "don't change" on both fields so the modal is safe to
 * open from any context — operator has to make a deliberate choice
 * before submit unlocks. This is also what makes the tenant-only
 * flow possible from the OpsDevices Move action (DE-160): leave the
 * group on "don't change", pick a new tenant, submit.
 */
import { useState } from 'react';
import {
  ARTOMEUI_ENV_GROUPS,
  ENV_GROUP_LABEL,
  useDevices,
  useSetDeviceGroup,
  useSetDeviceTenant,
  useTenants,
  type EnvGroup,
} from '../api/hooks';

interface MoveModalProps {
  thing: string;
  currentGroup: EnvGroup;
  onClose: () => void;
}

export function MoveModal({ thing, currentGroup, onClose }: MoveModalProps) {
  const setGroup = useSetDeviceGroup();
  const setTenant = useSetDeviceTenant();
  const tenants = useTenants();
  // DE-139 — current tenant for this thing pulled from the devices list
  // (already cached by the OpsDevices / OpsDeviceGroups / Dashboard routes).
  const devices = useDevices();
  const currentTenant =
    devices.data?.devices.find((d) => d.thing === thing)?.tenant_id ?? '';

  // DE-160 — default the group target to currentGroup (i.e. "don't change"),
  // so the modal can also drive a tenant-only move when opened from the
  // OpsDevices row action. Submit logic below only fires the group
  // mutation when target !== currentGroup.
  const [target, setTarget] = useState<EnvGroup>(currentGroup);

  // DE-139 — tenant move alongside env-group move. Empty `tenantTarget`
  // (initial state) means "no tenant change"; selecting a tenant or typing
  // a new one triggers the second mutation on submit.
  const [tenantTarget, setTenantTarget] = useState<string>('');
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [newTenantInput, setNewTenantInput] = useState('');
  const resolvedTenant = creatingTenant ? newTenantInput.trim() : tenantTarget;
  const tenantWillChange =
    resolvedTenant.length > 0 && resolvedTenant !== currentTenant;
  const otherTenants = (tenants.data?.tenants ?? []).filter((t) => t !== currentTenant);

  const groupWillChange = target !== currentGroup;

  const [error, setError] = useState<string | null>(null);
  const pending = setGroup.isPending || setTenant.isPending;
  const canSubmit = (groupWillChange || tenantWillChange) && !pending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    const summary: string[] = [];
    if (groupWillChange) {
      summary.push(
        `Move ${thing} from ${ENV_GROUP_LABEL[currentGroup]} to ${ENV_GROUP_LABEL[target]}`,
      );
    }
    if (tenantWillChange) {
      summary.push(
        groupWillChange
          ? `and switch tenant ${currentTenant || '(none)'} → ${resolvedTenant}`
          : `Switch ${thing} tenant ${currentTenant || '(none)'} → ${resolvedTenant}`,
      );
    }
    if (!window.confirm(`${summary.join(' ')}?`)) return;

    // Run only the mutations that actually change something. Tracking
    // which index each task corresponds to gives the partial-failure
    // banner enough context to point at the right side.
    const tasks: Array<Promise<unknown>> = [];
    const labels: string[] = [];
    if (groupWillChange) {
      tasks.push(setGroup.mutateAsync({ thing, group: target }));
      labels.push('env-group');
    }
    if (tenantWillChange) {
      tasks.push(setTenant.mutateAsync({ thing, tenant_id: resolvedTenant }));
      labels.push('tenant');
    }
    const results = await Promise.allSettled(tasks);
    const failed: string[] = [];
    results.forEach((r, i) => {
      if (r.status === 'rejected') failed.push(labels[i]!);
    });
    if (failed.length === 0) {
      onClose();
      return;
    }
    setError(
      `partial failure: ${failed.join(' + ')} mutation${
        failed.length > 1 ? 's' : ''
      } failed — retry?`,
    );
  };

  const submitLabel =
    pending ? 'Moving…' :
    groupWillChange && tenantWillChange ? 'Move group + tenant' :
    groupWillChange ? 'Move device' :
    tenantWillChange ? 'Move tenant' :
    'Move';

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal">
      <form onSubmit={submit} className="card w-full max-w-md space-y-4">
        <div>
          <h2 className="font-semibold">Move device</h2>
          <p className="text-xs text-muted">
            <span className="font-mono">{thing}</span>
            <br />
            Current group:{' '}
            <span className="pill-muted">{ENV_GROUP_LABEL[currentGroup]}</span>
            {currentTenant && (
              <>
                {' · '}tenant: <span className="pill-muted">{currentTenant}</span>
              </>
            )}
          </p>
        </div>
        <fieldset className="space-y-2">
          <legend className="text-xs text-muted mb-1">
            Group (leave on "don't change" to keep current)
          </legend>
          {/* DE-160 — "don't change" is the first radio (and the default)
              so an operator opening Move from /ops/devices can do a
              tenant-only move just like the old TenantMoveModal. */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="env-group"
              value=""
              checked={target === currentGroup}
              onChange={() => setTarget(currentGroup)}
            />
            <span className="text-muted">don't change</span>
          </label>
          {ARTOMEUI_ENV_GROUPS.filter((g) => g !== currentGroup).map((g) => (
            <label key={g} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="env-group"
                value={g}
                checked={target === g}
                onChange={() => setTarget(g)}
              />
              <span>{ENV_GROUP_LABEL[g]}</span>
              <span className="font-mono text-[11px] text-muted">{g}</span>
            </label>
          ))}
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="text-xs text-muted mb-1">
            Tenant (leave on "don't change" to keep current)
          </legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="tenant"
              value=""
              checked={!creatingTenant && tenantTarget === ''}
              onChange={() => {
                setCreatingTenant(false);
                setTenantTarget('');
              }}
            />
            <span className="text-muted">don't change</span>
          </label>
          {otherTenants.map((t) => (
            <label key={t} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="tenant"
                value={t}
                checked={!creatingTenant && tenantTarget === t}
                onChange={() => {
                  setCreatingTenant(false);
                  setTenantTarget(t);
                }}
              />
              <span className="font-mono">{t}</span>
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="tenant"
              value="__new__"
              checked={creatingTenant}
              onChange={() => setCreatingTenant(true)}
            />
            <span className="text-muted">create new tenant…</span>
            {creatingTenant && (
              <input
                type="text"
                value={newTenantInput}
                onChange={(e) => setNewTenantInput(e.target.value)}
                placeholder="tenant id"
                autoFocus
                className="px-2 py-1 text-sm border border-border rounded font-mono w-40"
              />
            )}
          </label>
        </fieldset>
        {(setGroup.error || setTenant.error || error) && (
          <p className="text-xs text-warn">
            {error ??
              (setGroup.error as Error | null)?.message ??
              (setTenant.error as Error | null)?.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted hover:text-fg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium disabled:opacity-50"
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
